import { Pipe, PipeTransform } from "@angular/core";
import { UserService } from "../../../../services/user-service.service";
import { DateTime } from 'luxon';
import { TranslateService } from "@ngx-translate/core";

/**
 * A pipe for formatting dates across the NHA App.
 * It always expects UTC and takes the user's time zone preferences into account
 */
@Pipe({
  name: 'nhaDate',
  pure: false //recalc when language changes
})
export class CustomDatePipe implements PipeTransform {

  public transform(value: Date | string,
    format: 'relativeTime' | 'relativeDay' | 'relativeDayAndTime' | 'time' | 'compact' | 'shortDate' | 'fullDate' | 'dateTime' = 'relativeTime',
    timezone?: string): string {
    const locale = this.translate.currentLang || 'en';
    switch (format) {
      case 'relativeTime':
        return CustomDatePipe.relativeTime(value, timezone ?? this.TimeZone, locale);
      case 'relativeDay':
        return CustomDatePipe.relativeDay(value, timezone ?? this.TimeZone, locale);
      case 'relativeDayAndTime':
        return CustomDatePipe.relativeDayAndTime(value, timezone ?? this.TimeZone, locale);
      case 'compact':
        return CustomDatePipe.compact(value, timezone ?? this.TimeZone);
      case 'time':
        return CustomDatePipe.formatTime(value, timezone ?? this.TimeZone);
      case 'shortDate': {
        const newDate: Date = CustomDatePipe.convertToDate(value, timezone ?? this.TimeZone);
        return newDate.toLocaleDateString(locale);
      }
      case 'fullDate': {
        const newDate: Date = CustomDatePipe.convertToDate(value, timezone ?? this.TimeZone);
        return newDate.toLocaleDateString(locale, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
      }
      case 'dateTime':
        return CustomDatePipe.dateTime(value, timezone ?? this.TimeZone, locale);
    }
  }

  public constructor(private userService: UserService,
                     private translate: TranslateService) {

  }

  /**The current user's timezone*/
  private get TimeZone(): string {
    return this.userService.CurrentUserProfile?.NotificationPrefs.Timezone ?? 'America/Detroit';
  }

  /**Get date as a relative date. For example, "5 minutes ago", "3 days ago", or "Just now"
   * If the date is in the future, it can display it like "In 2 days"
   */
  public static relativeTime(date: string | Date, timezone: string, locale: string = 'en'): string {
    const newDate: Date = CustomDatePipe.convertToDate(date, timezone);
    const diffMinutes = (newDate.getTime() - Date.now()) / 60000;

    if (diffMinutes > 0) {
      if (diffMinutes <= 60) return diffMinutes <= 1 ? 'In 1 minute' : `In ${Math.ceil(diffMinutes)} minutes`;
      if (diffMinutes <= 1440) return `In ${Math.ceil(diffMinutes / 60)} hours`;
      if (diffMinutes <= 10080) return `In ${Math.ceil(diffMinutes / 1440)} days`;
    } else {
      const ago = Math.abs(diffMinutes);
      if (ago <= 60) return ago <= 1 ? 'Just now' : `${Math.ceil(ago)} minutes ago`;
      if (ago <= 1440) return `${Math.ceil(ago / 60)} hours ago`;
      if (ago <= 10080) return `${Math.ceil(ago / 1440)} days ago`;
    }

    return newDate.toLocaleDateString(locale, { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' });
  }

  /**Absolute date and time in the given timezone. For example "Sep 15, 2026, 3:30 PM".
   * Unlike relativeDayAndTime this always shows the calendar date, so it reads unambiguously
   * for values that may be days or weeks out (e.g. a "paused until" time).*/
  public static dateTime(date: string | Date, timezone: string, locale: string = 'en'): string {
    const newDate: Date = CustomDatePipe.convertToDate(date, timezone);
    return newDate.toLocaleString(locale, {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: 'numeric', minute: '2-digit'
    });
  }

  /**Diregards the date and just displays the time in timezone correct format
   * For example 2:30 PM or 14:30*/
  public static formatTime(date: string | Date, timezone: string, locale: string = 'en'): string {
    const newDate = CustomDatePipe.convertToDate(date, timezone);
    const hours = newDate.getHours();
    const minutes = newDate.getMinutes().toString().padStart(2, '0');

    const is24Hour = !new Intl.DateTimeFormat(locale, { hour: 'numeric' })
      .formatToParts(new Date(2020, 0, 1, 13))
      .some(part => part.type === 'dayPeriod');

    if (is24Hour) {
      return `${hours}:${minutes}`;
    } else {
      const hour12 = hours % 12 || 12;
      const period = hours < 12 ? 'AM' : 'PM';
      return `${hour12}:${minutes} ${period}`;
    }
  }

  /**
   * Uses a simple relative day like 'Today', 'Yesterday', 'This Tuesday' or 'Last Wednesday'
   */
  public static relativeDay(date: string | Date, timezone: string, locale: string = 'en'): string {
    const newDate: Date = CustomDatePipe.convertToDate(date, timezone);
    const dayDiff = CustomDatePipe.getDayDiff(newDate);

    if (dayDiff === 0) return 'Today';
    if (dayDiff === 1) return 'Tomorrow';
    if (dayDiff === -1) return 'Yesterday';
    if (dayDiff > 1 && dayDiff <= 7) return 'This ' + newDate.toLocaleDateString(locale, { weekday: 'long' });
    if (dayDiff > 7 && dayDiff <= 14) return 'Next ' + newDate.toLocaleDateString(locale, { weekday: 'long' });
    if (dayDiff < -1 && dayDiff >= -7) return 'Last ' + newDate.toLocaleDateString(locale, { weekday: 'long' });

    return newDate.toLocaleDateString(locale, { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' });
  }

  /**
   * Both the relative day and time. Like "Last Tuesday at 3:55PM"
   */
  public static relativeDayAndTime(date: string | Date, timezone: string, locale: string = 'en'): string {
    const newDate: Date = CustomDatePipe.convertToDate(date, timezone);
    return CustomDatePipe.relativeDay(newDate, timezone, locale) + " at " + newDate.toLocaleTimeString([], {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  }

  /**
   * Compact relative time. Always shows magnitude only, no fallback to a date string.
   * Examples: 'Now', '5m', '2h', '1d', '3w'
   */
  public static compact(date: string | Date, timezone: string): string {
    const newDate: Date = CustomDatePipe.convertToDate(date, timezone);
    const diffMinutes = Math.abs((newDate.getTime() - Date.now()) / 60000);

    if (diffMinutes < 1) return 'Now';
    if (diffMinutes < 60) return `${Math.floor(diffMinutes)}m`;
    if (diffMinutes < 1440) return `${Math.floor(diffMinutes / 60)}h`;
    if (diffMinutes < 10080) return `${Math.floor(diffMinutes / 1440)}d`;
    return `${Math.floor(diffMinutes / 10080)}w`;
  }

  /**Converts to a Date object, handling both UTC timestamps and date-only values*/
  public static convertToDate(date: string | Date, timezone: string): Date {
    if (typeof date === 'string') {
      // Detect date-only values: midnight with no timezone indicator
      // These represent calendar dates (e.g., event dates), not UTC timestamps
      const hasTimezone = date.endsWith('Z') || /[+-]\d{2}:?\d{2}$/.test(date);
      const isMidnight = date.includes('T00:00:00');

      if (!hasTimezone && isMidnight) {
        // Date-only value - extract date components without timezone conversion
        const [y, m, d] = date.substring(0, 10).split('-').map(Number);
        return new Date(y, m - 1, d);
      }

      // Treat as UTC timestamp (original behavior)
      const isoString = date.endsWith('Z') ? date : date + 'Z';
      const d = new Date(isoString);
      if (isNaN(d.getTime())) throw new Error('Invalid date string: '+d);
      return this.convertUTCToTimezone(d, timezone);
    }
    return this.convertUTCToTimezone(date, timezone);
  }

  /**Convert the utc date to the timezone using luxon*/
  private static convertUTCToTimezone(date: Date, timezone: string): Date {
    const dt = DateTime.fromJSDate(date, { zone: 'utc' }).setZone(timezone);
    return new Date(dt.year, dt.month - 1, dt.day, dt.hour, dt.minute, dt.second, dt.millisecond);
  }

  /**Returns the number of days between today and the given date (positive = future, negative = past)*/
  private static getDayDiff(date: Date): number {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const target = new Date(date);
    target.setHours(0, 0, 0, 0);
    return Math.round((target.getTime() - today.getTime()) / 86400000);
  }
}
